// The prompt-built site as the portal holds it, and small pure helpers for
// changing it. The backend checks the whole site again on every save
// (aiSite/gate.ts), so these only need to keep the HTML well formed.

export const AI_SITE_KEY = "aiSite";

export const isAiSite = (map) => Boolean(map && typeof map === "object" && map.engine === "ai-html");

export const sectionsOf = (site) => site?.pages?.[0]?.sections || [];

// Returns a copy of the site with its section list replaced.
export const withSections = (site, sections) => ({
  ...site,
  pages: [{ ...(site.pages?.[0] || { slug: "", title: "Home" }), sections }],
});

export const updateSection = (site, id, patch) =>
  withSections(site, sectionsOf(site).map((s) => (s.id === id ? { ...s, ...patch } : s)));

// "header" and "footer" live outside the section list.
export const htmlOf = (site, id) =>
  id === "header" || id === "footer" ? site?.[id]?.html || "" : sectionsOf(site).find((s) => s.id === id)?.html || "";

export const setHtml = (site, id, html) =>
  id === "header" || id === "footer" ? { ...site, [id]: { html } } : updateSection(site, id, { html });

export const partName = (site, id) =>
  id === "header" ? "Header" : id === "footer" ? "Footer" : sectionsOf(site).find((s) => s.id === id)?.name || "Section";

// What the AI endpoints need to keep a change in line with the rest of the page.
export const outlineOf = (site) => ({
  language: site.language,
  designDirection: site.designDirection,
  theme: site.theme,
  sections: sectionsOf(site).map((s) => ({ id: s.id, name: s.name, brief: s.prompt })),
});

export const newSectionId = (site, name) => {
  const base = String(name || "section").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) || "section";
  const taken = new Set(["header", "footer", ...sectionsOf(site).map((s) => s.id)]);
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
  return id;
};

const escapeText = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Elements marked data-mh-text hold only text (and <br>), so their content
// runs from the opening tag to the first "</".
export const patchText = (html, id, text) => {
  const re = new RegExp(`(<[a-z0-9]+\\b[^>]*\\bdata-mh-text="${escapeRe(id)}"[^>]*>)([\\s\\S]*?)(</)`, "i");
  const value = escapeText(text).replace(/\r?\n/g, "<br>");
  return html.replace(re, (_m, open, _old, close) => `${open}${value}${close}`);
};

// Sets an attribute on an <img> tag, adding it when missing.
const setAttr = (tag, name, value) => {
  const re = new RegExp(`\\b${name}="[^"]*"`);
  return re.test(tag) ? tag.replace(re, `${name}="${value}"`) : tag.replace(/^<img\b/, `<img ${name}="${value}"`);
};

// Swaps a fixed photo (src "" removes it). alt is left as it was unless given.
export const patchImage = (html, id, src, alt) => {
  const re = new RegExp(`<img\\b[^>]*\\bdata-mh-img="${escapeRe(id)}"[^>]*>`, "i");
  return html.replace(re, (tag) => {
    if (!src) return "";
    let out = setAttr(tag, "src", src.replace(/"/g, "%22"));
    if (alt !== undefined) out = setAttr(out, "alt", escapeText(alt).replace(/"/g, "&quot;"));
    return out;
  });
};

const attr = (tag, name) => (tag.match(new RegExp(`\\b${name}="([^"]*)"`)) || [])[1] || "";

// The photos of one part of the page: the fixed ones the owner can replace,
// and which live lists supply the rest.
export const imagesOf = (html) => {
  const fixed = [];
  const live = new Set();
  for (const [tag] of String(html || "").matchAll(/<img\b[^>]*>/gi)) {
    const id = attr(tag, "data-mh-img");
    if (id) fixed.push({ id, src: attr(tag, "src").replace(/&amp;/g, "&"), alt: attr(tag, "alt") });
    const source = attr(tag, "data-mh-live");
    if (source) live.add(source);
  }
  return { fixed, live: [...live] };
};

// Where each kind of live data is edited in the portal.
export const LIVE_PAGES = {
  events: ["Events", "/user-dashboard/user-events"],
  news: ["News", "/user-dashboard/user-news"],
  announcements: ["Announcements", "/user-dashboard/user-announcements"],
  prayer: ["Prayer times", "/user-dashboard/prayer-times/main"],
  jummah: ["Jumu'ah times", "/user-dashboard/prayer-times/jummah"],
  masjid: ["Masjid information", "/user-dashboard/masjid-information"],
};

export const EXAMPLE_PROMPTS = [
  "Modern and elegant in deep green and gold, with a focus on Quran classes for children and adults.",
  "Simple, light and calm. Big, clear prayer times at the top, then events and how to find us.",
  "A warm community hub for young families: youth programmes, weekend school and volunteering.",
  "Classic and dignified, inspired by Andalusian architecture, with donations and Jumu'ah front and centre.",
];

export const STYLES = ["Modern", "Classic", "Minimal", "Warm", "Bold"];

// Ready-made sections for "+ Add section". The AI still designs them to match.
export const READY_SECTIONS = [
  ["Prayer times", "Today's five prayers (athan and iqamah) with the next-prayer countdown and the dates, this week's timetable (today highlighted) and the Jumu'ah times, all in one section."],
  ["Jumu'ah", "Friday prayer: khutbah and iqamah times for each Jumu'ah, and a short welcome."],
  ["Upcoming events", "The masjid's upcoming events as cards with image, date, time, venue and a register link."],
  ["News", "The latest news items as cards with image, date and a read-more link."],
  ["Contact", "A contact form plus the masjid's phone, email and social links."],
  ["Find us", "The masjid's address with a map."],
  ["Donate", "Why supporting the masjid matters, with a clear donate call to action."],
  ["Facilities", "The masjid's facilities and services, and prayer hall capacity."],
];

export const FONTS = [
  "Playfair Display", "Cormorant Garamond", "Lora", "Merriweather", "Amiri", "Cinzel", "DM Serif Display",
  "Inter", "Manrope", "DM Sans", "Poppins", "Nunito Sans", "Work Sans", "Outfit", "Cairo", "Tajawal",
];

export const COLOR_LABELS = [
  ["primary", "Main colour"],
  ["accent", "Accent"],
  ["canvas", "Page background"],
  ["surface", "Cards and panels"],
  ["ink", "Text"],
  ["muted", "Secondary text"],
];
